import React, { useState, useEffect } from 'react';
import { config } from '../config/environment';
import type { UserProfile, UpdateUserProfileRequest } from '../types/user';
import { GENDER_VALUES, GENDER_OPTIONS } from '../types/user';
import { useAuth } from '../context/AuthContext';

interface MyInfoPageProps {
  onBack: () => void;
  onLogout?: () => void;
}

// 임의의 아바타 배경색 생성 (user_id 기반)
const generateAvatarColor = (id: string): string => {
  const colors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-indigo-500',
    'bg-cyan-500',
  ];
  
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
};

// 이름의 첫 글자 추출
const getInitial = (name: string): string => {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
};

export const MyInfoPage: React.FC<MyInfoPageProps> = ({ onBack, onLogout }) => {
  const { logout } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);
  const [isRequiredProfileSetup, setIsRequiredProfileSetup] = useState(false);

  // 폼 입력 상태
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    gender: '',
    phone_number: '',
    height: ''
  });

  // 프로필 사진 처리
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);

  // 토큰 로드
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    setToken(storedToken);
  }, []);

  // 프로필 데이터 조회
  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${config.apiUrl}/user/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.user) {
          setProfile(data.user);
          setProfilePicturePreview(data.user.picture || null);
          setFormData({
            name: data.user.name || '',
            birth_date: data.user.birth_date || '',
            gender: data.user.gender || '',
            phone_number: data.user.phone_number || '',
            height: data.user.height ? String(data.user.height) : ''
          });
          
          // 프로필 완성도 확인
          if (!data.user.profileComplete) {
            setIsRequiredProfileSetup(true);
          }
        } else {
          throw new Error(data.error || '프로필 조회 실패');
        }
      } catch (err) {
        console.error('[MyInfoPage] 프로필 조회 오류:', err);
        setError(err instanceof Error ? err.message : '프로필을 조회할 수 없습니다');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [token]);

  // 필수 필드 미입력 시 페이지 이탈 방지
  useEffect(() => {
    if (!isRequiredProfileSetup) return;

    const isRequiredFieldsMissing = !(formData.birth_date && formData.gender && formData.phone_number);

    // beforeunload 이벤트 (뒤로가기, 새로고침, 탭 닫기 등)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRequiredFieldsMissing) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    if (isRequiredFieldsMissing) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isRequiredProfileSetup, formData]);

  // onBack 방지 (설정 페이지로 뒤로가기)
  const handleBackAttempt = () => {
    const isRequiredFieldsMissing = isRequiredProfileSetup && !(formData.birth_date && formData.gender && formData.phone_number);
    
    if (isRequiredFieldsMissing) {
      setShowIncompleteWarning(true);
      return;
    }
    
    onBack();
  };

  // 입력 필드 변경 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 프로필 사진 변경 처리
  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // 프로필 저장
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updateData: UpdateUserProfileRequest = {
        name: formData.name || undefined,
        birth_date: formData.birth_date || null,
        gender: (formData.gender || null) as 'M' | 'F' | 'N' | null,
        phone_number: formData.phone_number || null,
        height: formData.height ? parseFloat(formData.height) : null
      };

      const response = await fetch(`${config.apiUrl}/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setProfile(data.user);
        setSuccess('프로필이 저장되었습니다');
        
        // 프로필 완성도 확인
        if (data.user.profileComplete) {
          setIsRequiredProfileSetup(false);
        }
        
        setTimeout(() => setSuccess(null), 2000);
      } else {
        throw new Error(data.error || '저장 실패');
      }
    } catch (err) {
      console.error('[MyInfoPage] 프로필 저장 오류:', err);
      setError(err instanceof Error ? err.message : '프로필을 저장할 수 없습니다');
    } finally {
      setSaving(false);
    }
  };

  // 계정 탈퇴 처리
  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      setError(null);

      const response = await fetch(`${config.apiUrl}/user/withdraw`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // 로컬 스토리지 정리
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        
        // 앱 상태 초기화 및 로그인 페이지로 이동
        setShowWithdrawModal(false);
        setSuccess('계정이 탈퇴되었습니다. 로그인 페이지로 이동합니다.');
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        throw new Error(data.error || '탈퇴 실패');
      }
    } catch (err) {
      console.error('[MyInfoPage] 계정 탈퇴 오류:', err);
      setError(err instanceof Error ? err.message : '계정을 탈퇴할 수 없습니다');
      setWithdrawing(false);
    }
  };

  // 제공자 배지 렌더링
  const getProviderBadge = () => {
    if (!profile) return null;

    const providerEmojis: Record<string, string> = {
      google: '🔵',
      kakao: '💛',
      naver: '🟢',
    };

    const providerNames: Record<string, string> = {
      google: 'Google',
      kakao: 'Kakao',
      naver: 'Naver',
    };

    const provider = profile.provider as string;
    const emoji = providerEmojis[provider] || '⚪';
    const name = providerNames[provider] || provider;

    return `${emoji} ${name}`;
  };

  if (loading) {
    return (
      <div className="flex flex-col h-screen bg-slate-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">프로필 정보를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 bg-white border-b border-slate-200">
        <button
          onClick={handleBackAttempt}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>설정</span>
        </button>
        <h2 className="text-lg font-semibold text-slate-900">MY 정보</h2>
        <div className="w-10"></div>
      </div>

      {/* 컨텐츠 */}
      <div className="flex-1 overflow-y-auto pb-4">
        {error && (
          <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="mx-4 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            ✅ {success}
          </div>
        )}

        {profile && (
          <>
            {/* 로그인 정보 섹션*/}
            <div className="mx-4 mt-4 rounded-2xl bg-white shadow-sm px-4 py-3 border border-slate-100">
              <h3 className="text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <span className="text-[18px]">🔐</span>
                <span>로그인 정보</span>
              </h3>

              {/* 제공자 정보 */}
              <div className="py-3 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
                <div>
                  <div className="text-[12px] text-slate-600 mb-1">로그인 제공자</div>
                  <div className="text-[14px] font-medium text-slate-900">{getProviderBadge()}</div>
                </div>
                <button
                  onClick={() => {
                    logout();
                    if (onLogout) {
                      onLogout();
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px] font-semibold hover:bg-red-100 transition-colors"
                  type="button"
                >
                  로그아웃
                </button>
              </div>

              {/* 이메일 */}
              <div className="py-3 border-b border-slate-100 last:border-b-0">
                <div className="text-[12px] text-slate-600 mb-1">이메일</div>
                <div className="text-[14px] font-medium text-slate-900">{profile.email}</div>
              </div>

              {/* 프로필 이미지 (변경 가능) */}
              <div className="py-3 border-b border-slate-100 last:border-b-0">
                <div className="text-[12px] text-slate-600 mb-2">프로필 사진</div>
                <div className="flex items-center gap-3">
                  {profilePicturePreview ? (
                    <img
                      src={profilePicturePreview}
                      alt="프로필"
                      className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
                    />
                  ) : (
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg border-2 border-slate-200 ${generateAvatarColor(profile.id)}`}>
                      {getInitial(profile.name)}
                    </div>
                  )}
                  <label className="text-[12px] text-blue-500 cursor-pointer hover:text-blue-600 font-medium">
                    <span>변경</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* 프로필 정보 섹션 (편집 가능) */}
            <div className="mx-4 mt-4 rounded-2xl bg-white shadow-sm px-4 py-3 border border-slate-100">
              <h3 className="text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <span className="text-[18px]">👤</span>
                <span>프로필 정보</span>
              </h3>

              <div className="space-y-4">
                {/* 닉네임 */}
                <div>
                  <label className="text-[12px] text-slate-600 block mb-1">닉네임</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="닉네임을 입력하세요"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 생년월일 */}
                <div>
                  <label className="text-[12px] text-slate-600 block mb-1">생년월일</label>
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 성별 */}
                <div>
                  <label className="text-[12px] text-slate-600 block mb-1">성별</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">선택하기</option>
                    {GENDER_VALUES.map((value: string) => (
                      <option key={value} value={value}>
                        {GENDER_OPTIONS[value as keyof typeof GENDER_OPTIONS]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 신장 */}
                <div>
                  <label className="text-[12px] text-slate-600 block mb-1">신장 (cm)</label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    placeholder="170"
                    min="0"
                    max="300"
                    step="0.1"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* 연락처 */}
                <div>
                  <label className="text-[12px] text-slate-600 block mb-1">연락처</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="01012345678 또는 02-123-4567"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* 버튼 - 저장만 */}
              <div className="mt-6 pt-4 border-t border-slate-100">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg font-medium text-[14px] hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all"
                >
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>

            {/* 계정 정보 섹션 */}
            <div className="mx-4 mt-4 rounded-2xl bg-white shadow-sm px-4 py-3 border border-slate-100 mb-4">
              <h3 className="text-[13px] font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <span className="text-[18px]">ℹ️</span>
                <span>계정 정보</span>
              </h3>
              <div className="space-y-2 text-[12px] text-slate-600">
                <p>
                  <span className="font-medium">계정 생성:</span> {new Date(profile.created_at).toLocaleDateString('ko-KR')}
                </p>
                <p>
                  <span className="font-medium">마지막 수정:</span> {new Date(profile.updated_at).toLocaleDateString('ko-KR')}
                </p>
                <p className="pt-2 border-t border-slate-100 mt-3">
                  <button
                    onClick={() => setShowWithdrawModal(true)}
                    className="text-red-500 hover:text-red-600 font-medium transition-colors"
                  >
                    계정 탈퇴
                  </button>
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 계정 탈퇴 모달 */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div className="w-full rounded-t-2xl bg-white shadow-lg p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">계정을 탈퇴하시겠어요?</h3>
              <p className="text-sm text-slate-600 mt-2">
                탈퇴하면 모든 개인정보와 데이터가 삭제되며 복구할 수 없습니다.
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="w-full px-4 py-3 bg-red-500 text-white rounded-lg font-medium text-[14px] hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all"
              >
                {withdrawing ? '탈퇴 중...' : '탈퇴하기'}
              </button>
              <button
                onClick={() => setShowWithdrawModal(false)}
                disabled={withdrawing}
                className="w-full px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium text-[14px] hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 필수 필드 미입력 경고 모달 */}
      {showIncompleteWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div className="w-full rounded-t-2xl bg-white shadow-lg p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">필수 정보 입력 필요</h3>
              <p className="text-sm text-slate-600 mt-2">
                서비스 이용을 위해 다음 정보를 반드시 입력해주세요:
              </p>
              <ul className="text-sm text-slate-600 mt-3 space-y-1 ml-2">
                {!formData.birth_date && <li>• 생년월일</li>}
                {!formData.gender && <li>• 성별</li>}
                {!formData.phone_number && <li>• 연락처</li>}
              </ul>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => setShowIncompleteWarning(false)}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg font-medium text-[14px] hover:bg-blue-600 active:scale-95 transition-all"
              >
                정보 입력하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyInfoPage;
